import { ColDef, CellEditingStoppedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState, useRef, useCallback } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { useMST4060_R01Store } from "./Store_MST4060_R01";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { MST4060_R01DetailDto } from "./Dto_MST4060_R01";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { formatDate } from "src/utils/string/stringUtil";
import {
	InsertRowHandler,
	createCalendarCellClickHandler,
	CreateCellClickHandler,
	CustomCellClickAction,
	CreateCellDoubleClickHandler,
	CustomCellDoubleClickAction,
	GridCellConfig,
	createCellCssClassRules,
	createGridDropDownCommitAfter,
} from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { showWarning } from "src/utils/toast/toastUtil";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { useMST4060_R01Modal } from "./hooks/useMST4060_R01Modal";
import { useMST4060_R01CellChange } from "./hooks/MST4060_R01CellChange";
import { ShiftRangeSelectionConfig } from "src/utils/grid/useShiftRangeRowSelection";
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';


// 행 상태에 따라 편집 가능, 비활성, 포인터 표시를 결정하는 셀 규칙이다.

// 신규 행 추가 시 기본적으로 편집 모드를 허용할 컬럼 목록이다.
const DEFAULT_EDITABLE_COLUMNS = ["inspectType", "transportQty", "customCd", "inspectEmpno", "remark"];

// 사용자 컬럼 설정을 복원할 때 사용하는 필드별 한글 헤더명이다.
const headerNames: Record<string, string> = {
	rowIndex: "",
	inspectDt: "일자",
	inspectType: "변경내역",
	transportQty: "수량",
	eqmUnit: "단위",
	customCd: "거래처",
	customNm: "거래처명",
	inspectEmpno: "등록자",
	inspectEmpNm: "등록자명",
	remark: "비고",
};
// 입출고 그리드의 컬럼 순서·너비·표시 상태를 저장하는 키다.
const COLUMN_STATE_KEY = "MST4060_R01-detail-grid-column-state";


interface Props {
	gridRef: React.RefObject<AgGridReact<MST4060_R01DetailDto> | null>;
	defaultMST4060_R01DetailRow: MST4060_R01DetailDto;
}

// 기초재고와 월별 입출고 내역을 표시하고 행 편집·추가·삭제를 처리한다.
export const DetailTable_MST4060_R01 = (props: Props) => {
	const { gridRef, defaultMST4060_R01DetailRow } = props;

	const {
		MST4060_R01DetailList,
		selectedMST4060_R01DetailList, deletedMST4060_R01DetailList,
		insertMST4060_R01DetailList, removeMST4060_R01DetailList, updateMST4060_R01DetailList,
		setSelectedMST4060_R01DetailList, addDeletedMST4060_R01DetailList, initSelectedMST4060_R01DetailList,
		originalMST4060_R01DetailList,
		MST4060_R01GridCalendarState, setMST4060_R01GridCalendarState,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
		baseQty,
		editMST4060_R01,
		setEditMST4060_R01,
	} = useMST4060_R01Store();

	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } =
		MST4060_R01GridCalendarState;

	const { inspectionStatusList, unitList } = useCommonCodeStore();
	const transTypeList = useMemo(
		() => inspectionStatusList.filter((code) =>
			code.minorCd === "INCOMING" || code.minorCd === "RELEASE"),
		[inspectionStatusList],
	);

	const [clickable, setClickable] = useState(true);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: MST4060_R01DetailList,
		defaultGridData: defaultMST4060_R01DetailRow,
		selectedRow: selectedMST4060_R01DetailList,
		originalGridData: originalMST4060_R01DetailList,
		updateRow: (index: number, data: any) => updateMST4060_R01DetailList(index, data),
	});

	// 조회월 이전 누적 수량을 편집 불가능한 기초재고 가상행으로 구성한다.
	const baseRow = useMemo<MST4060_R01DetailDto>(() => ({
		...defaultMST4060_R01DetailRow,
		valueIndex: -1,
		inspectType: "",
		inspectTypeNm: "기초재고",
		transportQty: baseQty,
		isBaseRow: true,
		inserted: false,
		isModified: false,
	}), [baseQty, defaultMST4060_R01DetailRow]);

	// 기초재고 행을 실제 입출고 목록의 맨 앞에 결합한다.
	const gridRowData = useMemo(
		() => [baseRow, ...paddedGridData],
		[baseRow, paddedGridData],
	);

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ["customNm", "inspectEmpNm"],
		insertedDisabledCells: ["customNm", "inspectEmpNm"],
		commonDisabledCells: [],
		pointerCells: ["inspectDt", "inspectType", "transportQty", "eqmUnit", "customCd", "inspectEmpno", "remark"],
		insertedPointerCells: ["inspectDt", "inspectType", "transportQty", "eqmUnit", "customCd", "inspectEmpno", "remark"],
		commonPointerCells: [],
		alignLeftCells: [],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	// 선택·삭제·신규 상태를 반영한 공통 셀 클래스 규칙을 생성한다.
	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<MST4060_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedMST4060_R01DetailList,
			deletedList: deletedMST4060_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedMST4060_R01DetailList, deletedMST4060_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	// 신규 행의 삭제 버튼 클릭 시 목록에서 즉시 제거한다.
	// 현재 행번호 UI는 신규 행에만 삭제 버튼을 표시하므로 기존 저장 행 삭제 분기는 사용하지 않는다.
	const handleRemoveDetailRow = useCallback(
		(rowData: MST4060_R01DetailDto) => {
			if (rowData.isBaseRow) return;
			if (MST4060_R01DetailList.length <= 1) {
				showWarning("데이터가 한 개만 있을 때는 삭제가 불가능합니다.");
				return;
			}
			if (rowData.valueIndex === null || rowData.valueIndex === undefined) return;

			setClickable(false);

			if (rowData.inserted) {
				gridRef.current?.api.applyTransaction({ remove: [rowData] });
				removeMST4060_R01DetailList(rowData.valueIndex);
			} else {
				// 현재 UI에서는 사용 안 함: 기존 저장 행에는 삭제 버튼이 표시되지 않는다.
				addDeletedMST4060_R01DetailList([rowData.valueIndex.toString()]);
				gridRef.current?.api.refreshCells({ force: true });
			}
		},
		[
			MST4060_R01DetailList.length,
			gridRef,
			addDeletedMST4060_R01DetailList,
			removeMST4060_R01DetailList,
		],
	);

	// 삭제 표시된 행은 추가 편집을 차단한다.
	const isEditable = (params: any) => {
		if (!params.data) return false;
		if (params.data.isBaseRow) return false;
		const valueIndex = params.data.valueIndex?.toString() || "";
		const isDeleted = deletedMST4060_R01DetailList.includes(valueIndex);
		return !isDeleted;
	};

	// 일자 셀 편집 종료 후 해당 셀 위치에 공통 달력 팝업을 연다.
	const handleCellEditingStopped = useCallback(
		(e: CellEditingStoppedEvent<MST4060_R01DetailDto>) => {
			if (e.colDef.field !== "inspectDt") return;

			setTimeout(() => {
				const gridDiv = gridWrapperRef.current;
				if (!gridDiv) return;

				const cellSelector = `[row-index="${e.rowIndex}"] [col-id="inspectDt"]`;
				const cellElement = gridDiv.querySelector<HTMLElement>(cellSelector);

				if (cellElement) {
					const cellRect = cellElement.getBoundingClientRect();
					setMST4060_R01GridCalendarState({
						pickerPosition: {
							top: cellRect.bottom + window.scrollY,
							left: cellRect.left + window.scrollX,
						},
						targetRowIndex: e.rowIndex!,
						targetField: "inspectDt",
						pickerVisible: true,
					});
				}
			}, 0);
		},
		[setMST4060_R01GridCalendarState],
	);

	// 키보드 이동과 공통 상세 그리드 편집 이벤트를 구성한다.
	const cellEventHandlers = useDetailTableEvents({
		detailList: MST4060_R01DetailList,
		editableColumns: DEFAULT_EDITABLE_COLUMNS,
		keyDownConfig: { enableArrowNavigation: true },
	});

	// 일반 셀은 편집을 시작하고 일자 셀은 달력을 여는 클릭 동작 목록이다.
	const cellClickActions: CustomCellClickAction<MST4060_R01DetailDto>[] = useMemo(
		() => [
			{
				validate: (field) => DEFAULT_EDITABLE_COLUMNS.includes(field),
				action: (e) => {
					e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
				},
			},
			{
				validate: (field) => field === "inspectDt",
				action: createCalendarCellClickHandler(
					"inspectDt",
					MST4060_R01GridCalendarState,
					setMST4060_R01GridCalendarState,
				),
			},
		],
		[MST4060_R01GridCalendarState, setMST4060_R01GridCalendarState],
	);

	// 정의된 셀별 클릭 동작을 공통 그리드 클릭 핸들러로 결합한다.
	const handleCellClick = CreateCellClickHandler(MST4060_R01DetailList, "valueIndex", cellClickActions);

	const { settingCodeSearchModalProps } = useMST4060_R01Modal({
		updateMST4060_R01DetailList,
		gridRef,
	});

	// 거래처와 등록자 코드 셀 더블클릭 시 각 코드 검색 팝업을 연다.
	const cellDoubleClickActions: CustomCellDoubleClickAction<MST4060_R01DetailDto>[] = useMemo(
		() => [
			{
				validate: (field) => field === "customCd",
				action: (_e, foundData) => {
					settingCodeSearchModalProps(
						"거래처",
						foundData.customCd || "",
						false,
						"customCd",
						foundData,
						foundData.valueIndex,
					);
				},
			},
			{
				validate: (field) => field === "inspectEmpno",
				action: (_e, foundData) => {
					settingCodeSearchModalProps(
						"등록자",
						foundData.inspectEmpno || "",
						false,
						"inspectEmpno",
						foundData,
						foundData.valueIndex,
					);
				},
			},
		],
		[settingCodeSearchModalProps],
	);

	// 정의된 코드 검색 동작을 공통 더블클릭 핸들러로 결합한다.
	const handleCellDoubleClicked = CreateCellDoubleClickHandler(
		MST4060_R01DetailList,
		"valueIndex",
		cellDoubleClickActions,
	);

	// 드롭다운 선택 후 다음 편집 가능한 셀로 이동하는 공통 후처리다.
	const handleGridDropDownCommitAfter = useMemo(() =>
		createGridDropDownCommitAfter<MST4060_R01DetailDto>(DEFAULT_EDITABLE_COLUMNS),
	[],);


	// 입출고 내역의 입력 형식, 정렬, 팝업 및 행 추가/삭제 기능을 정의한다.
	const baseColumnDefs: ColDef<MST4060_R01DetailDto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig((params: MST4060_R01DetailDto) =>
				renderIndexCell(
					params,
					(innerParams: MST4060_R01DetailDto) =>
						getRowStyleFlags(innerParams, {
							deletedList: deletedMST4060_R01DetailList,
							selectedList: selectedMST4060_R01DetailList,
							dataList: MST4060_R01DetailList,
						}),
					(rowIndex: number, e: React.MouseEvent) => {
						const displayedRow = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data;
						if (!displayedRow || displayedRow.isBaseRow) return;
						const sourceRow = MST4060_R01DetailList.find(
							(row) => row.valueIndex === displayedRow.valueIndex,
						);
						if (!sourceRow) return;
						InsertRowHandler({
							event: e,
							dataList: MST4060_R01DetailList,
							rowData: sourceRow,
							insertRow: insertMST4060_R01DetailList,
							defaultRowData: defaultMST4060_R01DetailRow,
							setClickable,
						});
					},
					(rowData) => handleRemoveDetailRow(rowData),
				),
			),
			{
				headerName: "일자", colId: "inspectDt", field: "inspectDt", width: 120,
				editable: isEditable,
				valueFormatter: (params: any) => params.data?.isBaseRow
					? "기초재고"
					: (params.data?.inspectDt ? formatDate(new Date(params.data.inspectDt)) : ""),
				comparator: cellDateComparator,
			},
		{
				headerName: "변경내역", colId: "inspectType", field: "inspectType", width: 120,
				...CommonCodeColumn.options(transTypeList, true, CommonCodeColumn.change(updateMST4060_R01DetailList), handleGridDropDownCommitAfter),
			},
			{
				headerName: "수량", colId: "transportQty", field: "transportQty", width: 100,
				editable: isEditable,
				valueFormatter: (params) => (params.value ? params.value.toLocaleString() : "0"),
				cellStyle: { textAlign: "right" },
				comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
				pasteable: true,
				valueParser: (params) =>
					Number(String(params.newValue ?? "").replaceAll(',', "").trim()) || 0,
			},
			{
				headerName: "단위", colId: "eqmUnit", field: "eqmUnit", width: 70,
				valueGetter: () => editMST4060_R01.eqmUnit,
				...CommonCodeColumn.options(
					unitList,
					isEditable,
					(newValue) => setEditMST4060_R01({
						...editMST4060_R01,
						eqmUnit: newValue,
					}),
					handleGridDropDownCommitAfter,
				),
			},
			{
				headerName: "거래처", colId: "customCd", field: "customCd", width: 110,
				editable: isEditable,
				pasteable: true,
			},
			{
				headerName: "거래처명", colId: "customNm", field: "customNm", width: 150,
				editable: false,
			},
			{
				headerName: "등록자", colId: "inspectEmpno", field: "inspectEmpno", width: 90,
				editable: isEditable,
				pasteable: true,
			},
			{
				headerName: "등록자명", colId: "inspectEmpNm", field: "inspectEmpNm", width: 100,
				editable: false,
			},
			{
				headerName: "비고", colId: "remark", field: "remark", width: 200,
				editable: isEditable,
				pasteable: true,
			},
		],
		[
			deletedMST4060_R01DetailList,
			MST4060_R01DetailList,
			selectedMST4060_R01DetailList,
			transTypeList,
			unitList,
			editMST4060_R01.eqmUnit,
			setEditMST4060_R01,
			handleRemoveDetailRow,
		],
	);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	// 저장된 사용자 컬럼 설정을 기본 상세 컬럼에 병합한다.
	const columnDefs: ColDef<MST4060_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST4060_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState]);

	// Shift 범위 선택을 지원하되 삭제 표시된 행은 선택 대상에서 제외한다.
	const shiftRangeSelectionConfig: ShiftRangeSelectionConfig<MST4060_R01DetailDto> = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: "valueIndex",
		selectedIds: selectedMST4060_R01DetailList as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedMST4060_R01DetailList(ids.map(String)),
		isRowSelectable: (event) => {
			if (!clickable) {
				setClickable(true);
				return false;
			}
			if (event.data?.isBaseRow) return false;
			const valueIndex = event.data?.valueIndex;
			if (valueIndex === undefined || valueIndex === null) return false;
			return !deletedMST4060_R01DetailList.includes(String(valueIndex));
		},
	}), [selectedMST4060_R01DetailList, setSelectedMST4060_R01DetailList, clickable, setClickable, deletedMST4060_R01DetailList]);

	const { handleCellValueChanged } = useMST4060_R01CellChange({
		updateMST4060_R01DetailList,
		settingCodeSearchModalProps,
	});

	// 정렬된 화면 행 번호를 고유 식별자로 변환해 원본 상세 행을 찾는다.
	const getDetailByDisplayedRowIndex = useCallback((rowIndex: number) => {
		const rowData = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data;
		if (!rowData || rowData.isBaseRow) return undefined;
		return MST4060_R01DetailList.find(
			(detail) => detail.valueIndex === rowData.valueIndex,
		);
	}, [gridRef, MST4060_R01DetailList]);

	// 그리드 하단에 현재 입출고 내역 건수를 표시한다.
	const pinnedBottomRow = useMemo(
		() => [
			{
				rowIndex: "",
				inspectDt: `${MST4060_R01DetailList.length.toLocaleString()}건`,
				editable: false,
			},
		],
		[MST4060_R01DetailList],
	);

	return (
		<GridContextWrapper<MST4060_R01DetailDto>
			gridRef={gridRef}
			gridWrapperRef={gridWrapperRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			initSelected={initSelectedMST4060_R01DetailList}
			defaultRowData={defaultMST4060_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<MST4060_R01DetailDto>
				ref={gridRef}
				rowData={gridRowData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<MST4060_R01DetailDto>({
					idKey: "valueIndex",
					selectedList: selectedMST4060_R01DetailList,
					deletedList: deletedMST4060_R01DetailList,
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClicked}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellValueChanged}
				onCellEditingStopped={handleCellEditingStopped}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setMST4060_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(position) =>
					setMST4060_R01GridCalendarState({ pickerPosition: position })
				}
				onClear={() => {
					setMST4060_R01GridCalendarState({ targetDate: undefined });
				}}
				onChange={(date, rowIndex, targetField) => {
					if (rowIndex !== null && targetField) {
						const foundData = getDetailByDisplayedRowIndex(rowIndex);
						if (foundData) {
							updateMST4060_R01DetailList(foundData.valueIndex, {
								...foundData,
								[targetField]: formatDate(date ?? new Date()),
								isModified: true,
							});
						}
					}
				}}
				onClickLoad={(rowIndex, targetField) => {
					const foundData = getDetailByDisplayedRowIndex(rowIndex);
					if (!foundData) return;
					const date = foundData[targetField as keyof MST4060_R01DetailDto] as Date;
					setMST4060_R01GridCalendarState({
						targetDate: date ? new Date(date) : new Date(),
					});
				}}
			/>
		</GridContextWrapper>
	);
};
